// The curated lists of dubai-todo.com: the category strip, the "Curated for
// Your Mood" tiles and the areas. Each is a page at /[lang]/<slug> listing the
// tours whose `tours.themes` contain its `theme` (the tours_by_theme RPC).
//
// `tours.themes` is set in Postgres by tour_themes() from the rows of
// tour_theme_rules (supabase/sites/<site>.sql). The category strip's themes,
// the topics and the areas but downtown have rules there; the moods and
// downtown don't yet, so their lists stay empty and the pages show their empty
// state. The `theme` values here are the rule names to use.
//
// Data only — the shared components read it through SITE_UI (src/site-ui.ts);
// the helpers over it (collectionsOfKind, isCollectionIndexable…) are
// @todo/web-ui/lib/collections. Copy (titles, intros) lives in the i18n
// resources under `collections.<slug>`.

import type { SeeAlsoPage } from '@todo/web-ui/components/collection-guide';
import type { Collection, StripItem } from '@todo/web-ui/lib/collections';

/**
 * The guides (Sanity articles) a page links to, by the English version's slug:
 * one id for the article in every language, as the slugs are localized.
 */
export const GUIDE_KEYS = {
  desertSafari: 'dubai-desert-safari-morning-evening-overnight',
  withKids: 'dubai-with-kids',
  threeDays: 'dubai-in-3-days-itinerary',
  bestTime: 'best-time-to-visit-dubai-weather-by-month',
  freeThings: 'free-things-to-do-in-dubai',
  nyeFireworks: 'where-to-watch-new-years-eve-fireworks-dubai',
} as const;
export type GuideKey = (typeof GUIDE_KEYS)[keyof typeof GUIDE_KEYS];

const G = GUIDE_KEYS;

export const COLLECTIONS: readonly Collection[] = [
  // Category strip, in design order. "Yachts & Cruises" is the boats hub
  // (/boats, the yacht_charter tours — YACHT_THEME), not a collection — see CATEGORY_STRIP. Scenic flights took the
  // design's "Events & Festivals" slot: there is no events source, while Viator
  // has ~300 balloon and helicopter flights in Dubai.
  { slug: 'desert-safari', theme: 'desert_safari', kind: 'category', icon: 'M2 18l6-8 4 5 3-3 7 6M15 6.5a1.5 1.5 0 1 0 0 .01', guides: [G.desertSafari] },
  { slug: 'city-tours', theme: 'city_tour', kind: 'category', icon: 'M4 21V10l4-2v13M8 21V5l5-2v18M13 21V8l7 3v10M3 21h18' },
  { slug: 'attractions', theme: 'attraction', kind: 'category', icon: 'M4 7h16v3a2 2 0 0 0 0 4v3H4v-3a2 2 0 0 0 0-4zM10 7v10', guides: [G.threeDays] },
  { slug: 'water-sports', theme: 'water_sport', kind: 'category', icon: 'M2 16c2 0 2-1.5 4-1.5s2 1.5 4 1.5 2-1.5 4-1.5 2 1.5 4 1.5 2-1.5 4-1.5M2 20c2 0 2-1.5 4-1.5s2 1.5 4 1.5 2-1.5 4-1.5 2 1.5 4 1.5 2-1.5 4-1.5M8 11l4-7 5 4' },
  { slug: 'dhow-cruise', theme: 'dhow_cruise', kind: 'category', icon: 'M3 15h18l-3 4H6zM12 15V4M12 4c3 3 4 7 4 9h-4M12 6c-2 2-4 5-4 7h4' },
  { slug: 'family', theme: 'family', kind: 'category', icon: 'M8 8a2.5 2.5 0 1 0 0-.01M16 9.5a2 2 0 1 0 0-.01M3.5 20c.5-4 2.3-6.5 4.5-6.5s4 2.5 4.5 6.5M12.5 20c.4-3 1.8-5 3.5-5s3.1 2 3.5 5', guides: [G.withKids] },
  { slug: 'day-trips', theme: 'day_trip', kind: 'category', icon: 'M9 4 3 6v14l6-2 6 2 6-2V4l-6 2zM9 4v14M15 6v14' },
  { slug: 'scenic-flights', theme: 'scenic_flight', kind: 'category', icon: 'M12 3a6 6 0 0 0-6 6c0 3.5 3 6 4.5 8h3c1.5-2 4.5-4.5 4.5-8a6 6 0 0 0-6-6zM12 3c-1.7 1.6-2.5 3.6-2.5 6s.8 5.5 1 8M12 3c1.7 1.6 2.5 3.6 2.5 6s-.8 5.5-1 8M10 20h4v2h-4z' },

  // Topics: one well-searched activity inside a category, a small part of it —
  // not most of it, or the page repeats the category's. Not in the strip;
  // linked from their category's page.
  { slug: 'hot-air-balloon', theme: 'hot_air_balloon', kind: 'topic', parent: 'scenic-flights', icon: 'M12 3a6 6 0 0 0-6 6c0 3.5 3 6 4.5 8h3c1.5-2 4.5-4.5 4.5-8a6 6 0 0 0-6-6zM10 20h4v2h-4z' },
  { slug: 'helicopter-tour', theme: 'helicopter_tour', kind: 'topic', parent: 'scenic-flights', icon: 'M3 6h18M12 6v3M6 9h10a3 3 0 0 1 3 3v1a3 3 0 0 1-3 3H9a3 3 0 0 1-3-3zM19 12h3M9 16l-1 3M15 16l1 3M6 19h12' },
  { slug: 'quad-biking', theme: 'quad_biking', kind: 'topic', parent: 'desert-safari', icon: 'M6 17a3 3 0 1 0 0-.01M18 17a3 3 0 1 0 0-.01M9 17h6M8 11h8l2 6M6 14l2-3M11 11l1-4h3', guides: [G.desertSafari] },
  { slug: 'morning-desert-safari', theme: 'morning_desert_safari', kind: 'topic', parent: 'desert-safari', icon: 'M2 18c3-3 6-4 10-4s7 1 10 4M8 13a4 4 0 0 1 8 0M12 6V3M5.5 8.5 4 7M18.5 8.5 20 7', guides: [G.desertSafari] },
  // The tickets of one landmark, inside attractions (Aquaventure, Miracle
  // Garden, Ski Dubai, the Dubai Aquarium too: day tickets, not family
  // activities of their own).
  { slug: 'burj-khalifa', theme: 'burj_khalifa', kind: 'topic', parent: 'attractions', icon: 'M12 2v20M10 22V8l2-6 2 6v14M7 22v-8h3M14 14h3v8M5 22h14', guides: [G.threeDays] },
  { slug: 'dubai-frame', theme: 'dubai_frame', kind: 'topic', parent: 'attractions', icon: 'M5 3h14v18H5zM9 7h6v14M3 21h18', guides: [G.threeDays] },
  { slug: 'aquaventure', theme: 'aquaventure', kind: 'topic', parent: 'attractions', icon: 'M5 20V9a4 4 0 0 1 8 0v3a4 4 0 0 0 7 2.5M2 20c2 0 2-1.5 4-1.5s2 1.5 4 1.5 2-1.5 4-1.5 2 1.5 4 1.5 2-1.5 4-1.5', guides: [G.withKids] },
  { slug: 'miracle-garden', theme: 'miracle_garden', kind: 'topic', parent: 'attractions', icon: 'M12 9a3 3 0 1 0 0-.01M12 6V3M12 15v-3M9 9H6M18 9h-3M12 22v-7M12 18c-2-2-5-2-7-1M12 18c2-2 5-2 7-1', guides: [G.bestTime] },
  // Global Village is a festival park more than a ticket, but its tickets sit
  // with the landmarks' — a seasonal evening out, like the Miracle Garden.
  { slug: 'global-village', theme: 'global_village', kind: 'topic', parent: 'attractions', icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 1 0 0-18zM3 12h18M12 3c2.5 2.5 3.5 5.5 3.5 9s-1 6.5-3.5 9M12 3c-2.5 2.5-3.5 5.5-3.5 9s1 6.5 3.5 9', guides: [G.bestTime, G.withKids] },
  { slug: 'ski-dubai', theme: 'ski_dubai', kind: 'topic', parent: 'attractions', icon: 'M3 20l18-6M8 18.3V9M14 16.3V5M5 9h6M11 5h6M12 3v2M18 8a1 1 0 1 0 0-.01', guides: [G.withKids] },
  { slug: 'dubai-aquarium', theme: 'dubai_aquarium', kind: 'topic', parent: 'attractions', icon: 'M2 12c3-5 9-6 14-3l4-3v12l-4-3c-5 3-11 2-14-3zM6 11.5a1 1 0 1 0 0-.01', guides: [G.withKids] },
  // Abu Dhabi's theme parks, inside day-trips: from Dubai, a park day is a day trip.
  { slug: 'yas-island', theme: 'yas_island', kind: 'topic', parent: 'day-trips', icon: 'M2 20h20M5 20v-8M10 20v-5M15 20v-8M19 20V9M2 13c3-6 6-6 8 0s5 3 6-3 4-4 6-1', guides: [G.withKids] },

  // "Curated for Your Mood".
  { slug: 'date-night', theme: 'date_night', kind: 'mood', icon: 'M12 20s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.3a4.3 4.3 0 0 1 7.5 2.5C19.5 15.4 12 20 12 20z' },
  { slug: 'beach-days', theme: 'beach', kind: 'mood', icon: 'M12 4a7 7 0 0 1 7 7H5a7 7 0 0 1 7-7zM12 11v9M3 20h18M12 4V3' },
  { slug: 'art-and-culture', theme: 'culture', kind: 'mood', icon: 'M4 21h16M5 21V10M19 21V10M9 21V10M15 21V10M3 10l9-6 9 6z' },
  { slug: 'luxury', theme: 'luxury', kind: 'mood', icon: 'M6 4h12l3 5-9 11L3 9zM3 9h18M9 4l3 16M15 4l-3 16' },
  { slug: 'adventure', theme: 'adventure', kind: 'mood', icon: 'M3 20l6-11 4 6 2-3 6 8zM14 7a2 2 0 1 0 0-.01' },

  // Areas (footer, "Areas"). Dubai is one `cities` row; an area is a theme
  // too, e.g. keywords "marina|jbr" for dubai-marina.
  { slug: 'downtown-dubai', theme: 'area_downtown', kind: 'area', icon: 'M12 2v20M9 22V9l3-4 3 4v13M6 22v-7h3M15 15h3v7' },
  { slug: 'dubai-marina', theme: 'area_marina', kind: 'area', icon: 'M5 22V8h4v14M15 22V4h4v18M2 22h20', guides: [G.freeThings] },
  { slug: 'palm-jumeirah', theme: 'area_palm', kind: 'area', icon: 'M12 22V10M12 10C9 6 5 6 3 8M12 10c3-4 7-4 9-2M12 10c-1-4-4-6-7-6M12 10c1-4 4-6 7-6' },
  { slug: 'old-dubai', theme: 'area_old_dubai', kind: 'area', icon: 'M4 21V11l8-6 8 6v10M9 21v-6h6v6M2 21h20', guides: [G.threeDays, G.freeThings] },
];

/**
 * The theme of the /boats hub ("Yachts & Cruises" in the strip): not a
 * collection page, but a tour_theme_rules theme like theirs — every
 * Click&Boat boat trip plus Viator's yacht tours (supabase/sites/dubai.sql).
 */
export const YACHT_THEME = 'yacht_charter';

/** The "Read also" guides of the hubs that aren't collections (/tours, /events). */
export const HUB_GUIDES: Readonly<Record<string, readonly GuideKey[]>> = {
  tours: [G.threeDays],
  events: [G.bestTime],
  'new-years-eve': [G.nyeFireworks],
};

/** The New Year's Eve hub (src/app/[lang]/new-years-eve.tsx): a page of its own, not a collection. */
export const NYE_PATH = 'new-years-eve';
const NYE_LINK: SeeAlsoPage = { path: NYE_PATH, labelKey: 'collections.new-years-eve.title' };

/**
 * Pages beyond a collection's own topics in its "See also" line (TopicLinks),
 * by the page's path: the New Year's Eve hub from the pages whose readers
 * book its cruises and parties.
 */
export const SEE_ALSO_PAGES: Readonly<Record<string, readonly SeeAlsoPage[]>> = {
  'dhow-cruise': [NYE_LINK],
  boats: [NYE_LINK],
  events: [NYE_LINK],
};

/**
 * The eight entries of the category strip (a 4×2 grid on phones), in design
 * order. Family is left out of it — still a collection, linked from the home
 * page's category guide.
 */
export const CATEGORY_STRIP: StripItem[] = [
  { kind: 'collection', collection: COLLECTIONS.find((c) => c.slug === 'desert-safari')! },
  { kind: 'page', path: 'boats', labelKey: 'strip.boats', icon: 'M12 3v13M12 4l6 11h-6M12 6l-5 9h5M3 18h18l-2 3H5z' },
  ...COLLECTIONS.filter((c) => c.kind === 'category' && c.slug !== 'desert-safari' && c.slug !== 'family').map(
    (collection) => ({ kind: 'collection' as const, collection }),
  ),
];
